import { bytesAreValidPdf } from '@cadstrom/cadstrom-shared';
import { queryOptions, skipToken } from '@tanstack/react-query';

import type { Auth0Client } from '@auth0/auth0-spa-js';

/**
 * The resolved contents of a stored file, fetched as raw bytes. The bytes are always a PDF; a file
 * whose contents are not fails the fetch instead.
 */
export interface FileContentsQueryData {
	/** The file's stored bytes. */
	contents: ArrayBuffer;
	/** The stored file name, when the response carried one. */
	fileName: string | undefined;
}

/**
 * A failed file-contents request, carrying the HTTP status so callers can
 * distinguish a missing file (404) from transport or server failures.
 */
export class FileContentsRequestError extends Error {
	constructor(
		/** The HTTP status code of the failed response. */
		public readonly status: number,
		message: string,
		/** How long the server asked the client to wait before retrying, when it said. */
		public readonly retryAfterMs?: number,
	) {
		super(message);
		this.name = 'FileContentsRequestError';
	}

	/**
	 * Reads a `Retry-After` header value, in either its delay-seconds or HTTP-date form.
	 * @param header - The raw header value, or null when absent.
	 * @param now - The current time, in epoch milliseconds.
	 * @returns The requested wait in milliseconds, never negative, or undefined when the header is
	 * absent or unreadable.
	 */
	public static parseRetryAfter(header: string | null, now: number = Date.now()): number | undefined {
		if (header === null) {
			return undefined;
		}
		const trimmed = header.trim();
		if (/^\d+$/.test(trimmed)) {
			return Number(trimmed) * 1000;
		}
		const at = Date.parse(trimmed);
		return Number.isNaN(at) ? undefined : Math.max(at - now, 0);
	}

	/**
	 * Whether a caught error is a file-contents 404, i.e. the referenced file or its contents are
	 * missing.
	 * @param error - The caught value.
	 * @returns Whether it reports a missing file.
	 */
	public static isFileNotFound(error: unknown): boolean {
		return error instanceof FileContentsRequestError && error.status === 404;
	}

	/**
	 * Whether a caught error is a file-contents failure that may clear on its own: the request never
	 * got a response, the server failed, or it asked the client to slow down. A missing file or a
	 * refused request is not transient.
	 * @param error - The caught value.
	 * @returns Whether repeating the request could succeed.
	 */
	public static isTransient(error: unknown): boolean {
		if (error instanceof TypeError) {
			return true;
		}
		if (!(error instanceof FileContentsRequestError)) {
			return false;
		}
		return error.status >= 500 || error.status === 408 || error.status === 429;
	}
}

/**
 * A stored file whose bytes are not a PDF, and so cannot be displayed.
 */
export class InvalidPdfError extends Error {
	constructor(
		/** The stored file name, when the response carried one. */
		public readonly fileName: string | undefined,
		message: string,
	) {
		super(message);
		this.name = 'InvalidPdfError';
	}

	/**
	 * Whether a caught error reports a stored file whose bytes are not a PDF.
	 * @param error - The caught value.
	 * @returns Whether it reports unreadable contents.
	 */
	public static isInvalidPdf(error: unknown): boolean {
		return error instanceof InvalidPdfError;
	}
}

/** How many times a transient file-contents failure is retried before the query fails. */
export const FILE_CONTENTS_MAX_RETRIES = 3;

/** The longest wait between two file-contents retries. */
const FILE_CONTENTS_MAX_RETRY_DELAY_MS = 8000;

/**
 * Retry policy for file-contents queries: a transient failure is retried a few times, anything else
 * fails at once. A server that asks for a longer wait than the retry cap is not retried, so the
 * failure surfaces instead of the query sitting idle.
 * @param failureCount - How many retries have already been made.
 * @param error - The error the latest attempt threw.
 * @returns Whether the query should be retried.
 */
export function shouldRetryFileContents(failureCount: number, error: unknown): boolean {
	if (error instanceof FileContentsRequestError && (error.retryAfterMs ?? 0) > FILE_CONTENTS_MAX_RETRY_DELAY_MS) {
		return false;
	}
	return failureCount < FILE_CONTENTS_MAX_RETRIES && FileContentsRequestError.isTransient(error);
}

/**
 * The wait before a file-contents retry, doubling from one second and capped, and never shorter
 * than a wait the server asked for.
 * @param failureCount - How many retries have already been made, so 0 before the first retry.
 * @param error - The error the latest attempt threw.
 * @returns The delay in milliseconds.
 */
export function fileContentsRetryDelay(failureCount: number, error?: unknown): number {
	const backoff = Math.min(1000 * 2 ** failureCount, FILE_CONTENTS_MAX_RETRY_DELAY_MS);
	const requested = error instanceof FileContentsRequestError ? (error.retryAfterMs ?? 0) : 0;
	return Math.max(backoff, requested);
}

/**
 * Extracts the file name from a `Content-Disposition` header value, preferring
 * the RFC 5987 `filename*` form over the ASCII fallback.
 * @param header - The raw header value, or null when absent.
 * @returns The decoded file name, or undefined when none can be read.
 */
export function parseContentDispositionFileName(header: string | null): string | undefined {
	if (header === null) {
		return undefined;
	}

	const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(header);
	if (encodedMatch?.[1] !== undefined) {
		try {
			return decodeURIComponent(encodedMatch[1]);
		} catch {
			// Malformed percent-encoding; fall back to the plain filename.
		}
	}

	const plainMatch = /filename="([^"]+)"/i.exec(header);
	return plainMatch?.[1];
}

/**
 * Fetches a stored file's bytes from the authenticated file-contents endpoint.
 * @param auth0Client - Auth0 client used to obtain the bearer token.
 * @param fileId - The file to fetch.
 * @param signal - Optional abort signal for the request.
 * @returns The file's bytes and its stored name when known.
 * @throws FileContentsRequestError When the response is not OK, with its HTTP status.
 * @throws InvalidPdfError When the fetched bytes are not a PDF.
 */
export async function fetchFileContents(
	auth0Client: Auth0Client,
	fileId: string,
	signal?: AbortSignal,
): Promise<FileContentsQueryData> {
	const token = await auth0Client.getTokenSilently();
	const res = await fetch(`/api/v1/files/${encodeURIComponent(fileId)}/contents`, {
		headers: { Authorization: `Bearer ${token}` },
		signal,
	});

	if (!res.ok) {
		throw new FileContentsRequestError(
			res.status,
			`Failed to fetch contents of file ${fileId}: ${res.status} ${res.statusText}`,
			FileContentsRequestError.parseRetryAfter(res.headers.get('Retry-After')),
		);
	}

	const contents = await res.arrayBuffer();
	const fileName = parseContentDispositionFileName(res.headers.get('Content-Disposition'));

	if (!bytesAreValidPdf(new Uint8Array(contents))) {
		throw new InvalidPdfError(fileName, `Contents of file ${fileId} are not a PDF`);
	}

	return { contents, fileName };
}

/**
 * The query key for a file-contents fetch, shared so mutations can reset the
 * cached entry after replacing a file reference.
 * @param fileId - The file the entry caches, or null for the disabled query.
 * @returns The query key.
 */
export function fileContentsQueryKey(fileId: string | null): readonly unknown[] {
	return ['file-contents', fileId];
}

/**
 * Builds query options for fetching a file's bytes. The query is disabled (via
 * `skipToken`) when `fileId` is nullish. Fetched bytes are kept fresh for the
 * cache entry's lifetime and never refetched while cached. A transient failure is retried a few
 * times before the query reports it.
 * @param auth0Client - Auth0 client used to obtain the bearer token.
 * @param fileId - The file to fetch, or null/undefined to disable the query.
 * @returns Typed query options for `useQuery`.
 */
export function fileContentsQueryOptions(auth0Client: Auth0Client, fileId: string | null | undefined) {
	const resolvedFileId = fileId ?? null;
	return queryOptions({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps -- the Auth0 client is an app-wide singleton; the token it mints does not change the fetched bytes
		queryKey: fileContentsQueryKey(resolvedFileId),
		queryFn:
			resolvedFileId === null ? skipToken : (
				({ signal }: { signal: AbortSignal }) => fetchFileContents(auth0Client, resolvedFileId, signal)
			),
		staleTime: Infinity,
		retry: shouldRetryFileContents,
		retryDelay: fileContentsRetryDelay,
	});
}

/**
 * Builds query options for fetching a known file's bytes, for callers that suspend. Shares its cache
 * entry and retry policy with {@link fileContentsQueryOptions}.
 * @param auth0Client - Auth0 client used to obtain the bearer token.
 * @param fileId - The file to fetch.
 * @returns Typed query options for `useSuspenseQuery`.
 */
export function fileContentsSuspenseQueryOptions(auth0Client: Auth0Client, fileId: string) {
	return queryOptions({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps -- the Auth0 client is an app-wide singleton; the token it mints does not change the fetched bytes
		queryKey: fileContentsQueryKey(fileId),
		queryFn: ({ signal }: { signal: AbortSignal }) => fetchFileContents(auth0Client, fileId, signal),
		staleTime: Infinity,
		retry: shouldRetryFileContents,
		retryDelay: fileContentsRetryDelay,
	});
}
